import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSeo, buildBreadcrumbSchema } from '@/hooks/useSeo';
import { collections, categories } from '@/mocks/collections';
import CollectionCard from '@/components/feature/CollectionCard';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

type SortKey = 'updated' | 'hot' | 'nodes';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'updated', label: '最近更新' },
  { key: 'hot', label: '热度最高' },
  { key: 'nodes', label: '节点最多' },
];

export default function Collections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? 'all';
  const sort = (searchParams.get('sort') as SortKey) ?? 'updated';
  const query = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(query);

  useSeo({
    title: '合集库 - 看瓜爆料全部专题汇总 | 看瓜爆料合集',
    description:
      '看瓜爆料合集库汇总全部娱乐专题，支持关键词搜索、分类筛选与热度排序。找事件时间线、人物梳理、完整翻车复盘，就在看瓜爆料合集库。',
    keywords: '看瓜爆料合集,看瓜爆料汇总,看瓜爆料专题,看瓜爆料全集,娱乐专题合集',
    canonical: '/collections',
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    let list = collections.filter((item) => {
      const matchCategory = activeCategory === 'all' || item.categorySlug === activeCategory;
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });

    list = [...list].sort((a, b) => {
      if (sort === 'hot') return b.views - a.views;
      if (sort === 'nodes') return b.entries.length - a.entries.length;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
    return list;
  }, [activeCategory, query, sort]);

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '看瓜爆料合集库',
    description: '看瓜爆料全部合集专题汇总列表。',
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: filtered.length,
      itemListElement: filtered.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.title,
        url: `https://ijlvv.cn/collections/${item.id}`,
      })),
    },
  };

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: '首页', path: '/' },
    { name: '合集库', path: '/collections' },
  ]);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-10 py-8 md:py-12">
          <Breadcrumbs items={[{ name: '首页', path: '/' }, { name: '合集库' }]} />
          <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl text-foreground-950">
            看瓜爆料合集库
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
            全部 {collections.length} 个已打包专题都在这里。可以按关键词搜索，也可以先选一个分类，再决定看哪条时间线。
          </p>
        </div>
      </div>

      <section className="sticky top-16 md:top-20 z-40 bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="w-full px-4 md:px-10 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <form
              className="flex items-center gap-2 flex-1 h-11 px-3 rounded-md border border-background-300 bg-background-50"
              onSubmit={(e) => {
                e.preventDefault();
                updateParam('q', keyword.trim());
              }}
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <label className="flex-1">
                <span className="sr-only">搜索合集</span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索事件、人物或关键词"
                  className="w-full bg-transparent border-0 text-sm label-font text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </label>
              {keyword && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => {
                    setKeyword('');
                    updateParam('q', '');
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
              <button
                type="submit"
                className="h-8 px-4 rounded-md bg-foreground-900 text-background-50 text-xs label-font whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>

            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs label-font text-foreground-500 whitespace-nowrap">排序</span>
              <div className="flex gap-1 p-1 rounded-full bg-background-200">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => updateParam('sort', option.key)}
                    className={`px-3 py-1.5 rounded-full text-xs label-font whitespace-nowrap transition-colors cursor-pointer ${
                      sort === option.key
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div id="categories" className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => updateParam('category', 'all')}
              className={`px-3 py-1.5 rounded-md text-xs label-font whitespace-nowrap transition-colors cursor-pointer border ${
                activeCategory === 'all'
                  ? 'bg-primary-500 text-background-50 border-primary-500'
                  : 'bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300'
              }`}
            >
              全部分类
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => updateParam('category', cat.slug)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs label-font whitespace-nowrap transition-colors cursor-pointer border ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50 border-primary-500'
                    : 'bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300'
                }`}
              >
                <i className={cat.icon}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-10 py-10 md:py-14">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950">
            {activeCategory === 'all'
              ? '全部专题'
              : categories.find((c) => c.slug === activeCategory)?.name}
            <span className="ml-2 text-sm font-normal text-foreground-500 label-font">
              共 {filtered.length} 个
            </span>
          </h2>
          {query && (
            <span className="text-xs label-font text-foreground-500">
              关键词：{query}
            </span>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((item, index) => (
              <CollectionCard key={item.id} item={item} index={index} />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center rounded-lg border border-dashed border-background-300">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">
              没有找到匹配的专题，换个关键词试试。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setSearchParams({}, { replace: true });
              }}
              className="mt-4 px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm label-font hover:bg-primary-600 transition-colors cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        )}
      </section>
    </>
  );
}